import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { BlankDocumentEditor } from "@/components/ai-documents/blank-document-editor";
import { Link } from "@/core/lib/navigation";
import { useSession } from "@/core/lib/session-context";
import { companyQuery, documentQuery, profileQuery } from "@/lib/loaders";

/**
 * Write a document from scratch: the rich-text editor, empty, with the same
 * fields templates use. `?doc=` re-opens a saved draft; a document that already
 * has output lives at `/document/{id}` unless "Edit form" asks for the editor.
 */
export const Route = createFileRoute("/_workspace/generate/blank")({
  validateSearch: (search: Record<string, unknown>) => ({
    doc: typeof search.doc === "string" ? search.doc : undefined,
    view: typeof search.view === "string" ? search.view : undefined,
  }),
  loaderDeps: ({ search }) => ({ doc: search.doc, view: search.view }),
  loader: async ({ context, deps }) => {
    if (deps.doc && deps.view !== "form") {
      // Best-effort: this lookup only powers the redirect.
      const existing = await context.queryClient
        .ensureQueryData(documentQuery(deps.doc))
        .catch(() => null);
      if (existing?.output) {
        throw redirect({ to: "/document/$id", params: { id: deps.doc } });
      }
    }
    await Promise.all([
      context.queryClient.ensureQueryData(companyQuery),
      context.queryClient.ensureQueryData(profileQuery),
    ]);
  },
  component: BlankDocumentPage,
});

function BlankDocumentPage() {
  const { doc, view } = Route.useSearch();
  const { data: company } = useQuery(companyQuery);
  const { data: profile } = useQuery(profileQuery);
  const session = useSession();

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div className="space-y-3">
          <Link
            href="/generate"
            className="inline-flex items-center gap-1.5 text-muted-foreground text-sm underline-offset-4 hover:underline"
          >
            <ArrowLeftIcon className="size-4" />
            All documents
          </Link>
          <div className="space-y-1">
            <h1 className="font-bold text-2xl tracking-tight">Blank document</h1>
            <p className="text-muted-foreground text-sm">
              Write it yourself. Insert fields to fill in each employee’s details.
            </p>
          </div>
        </div>
        <BlankDocumentEditor
          company={company ?? null}
          user={{ name: session.user.name, email: session.user.email }}
          seedDocId={doc}
          savedSignature={profile?.signature ?? ""}
          defaultSignerName={session.user.name || company?.issuerName || profile?.displayName || ""}
          forceForm={view === "form"}
        />
      </div>
    </main>
  );
}
